import type { FC } from "react";
import { useMemo } from "react";
import type { ProgressProps } from "@ptsd/uikit";
import { Card, Progress } from "@ptsd/uikit";
import { useTranslation } from "react-i18next";
import type { GetThemeValueForKey } from "tamagui";
import { Text, XStack, YStack } from "tamagui";

import { api } from "~/utils/api";
import { OverviewCard, OverviewDiff } from "./overview-common";

export const OverviewMetrics: FC = () => {
  const { t } = useTranslation("overview");

  const [data] = api.dashboard.getPatientInvolvementCounts.useSuspenseQuery();

  return (
    <OverviewCard title={t("metrics.title")}>
      <XStack width="100%" flex={1} gap={"$2"} alignItems="stretch">
        {data.map((item) =>
          isNaN(+item.percentage) ? (
            <MetricsCard
              key={item.desc}
              unit="points"
              variant="success"
              title={item.desc}
              value={item.count}
              diff={0}
            />
          ) : (
            <MetricsCard
              key={item.desc}
              unit="point_percent"
              variant="success"
              title={item.desc}
              value={+item.percentage}
              points={item.count}
            />
          ),
        )}
      </XStack>
    </OverviewCard>
  );
};

interface MetricsBaseProps {
  title: string;
  value: number | undefined;
  variant?: ProgressProps["variant"];
  empty?: boolean;
}

interface MetricsCardLevelProps extends MetricsBaseProps {
  unit: "level";
}

interface MetricsCardDiffProps extends MetricsBaseProps {
  unit: "percent" | "points";
  diff: number | null | undefined;
}

interface MetricsCardPointAndPercentProps extends MetricsBaseProps {
  unit: "point_percent";
  points: number | null | undefined;
}

type MetricsCardProps =
  | MetricsCardDiffProps
  | MetricsCardLevelProps
  | MetricsCardPointAndPercentProps;

const MetricsCard: FC<MetricsCardProps> = ({
  title,
  value = 0,
  empty = false,
  ...rest
}) => {
  const { valueColor, titleColor } = useMemo<{
    valueColor: GetThemeValueForKey<"color">;
    titleColor?: GetThemeValueForKey<"color">;
  }>(() => {
    if (empty) return { valueColor: "$gray-200", titleColor: "$gray-200" };

    switch (rest.variant) {
      case "danger":
        return { valueColor: "$darkpink-500" };
      default:
        return { valueColor: "$color" };
    }
  }, [rest.variant, empty]);

  return (
    <Card
      flex={1}
      flexBasis={1}
      shadow="md"
      paddingVertical={"$3"}
      paddingHorizontal={"$3"}
      backgroundColor={"$warm-snow-500"}
      borderRadius={"$8"}
      borderWidth={"$0.25"}
      borderColor={"$gray-600"}
      minWidth={240}
    >
      <YStack gap={"$2"}>
        <XStack width="100%" gap={"$1"}>
          {rest.unit === "level" && (
            <XStack>
              <Text
                fontFamily={"$body"}
                fontSize={"$7"}
                fontWeight={"$6"}
                color={valueColor}
              >
                {value}
              </Text>
              <Text fontFamily={"$body"} fontSize={"$7"} color="$gray-200">
                /100
              </Text>
            </XStack>
          )}
          {(rest.unit === "percent" || rest.unit === "points") && (
            <XStack alignItems="center" gap={"$1"}>
              <Text
                fontFamily={"$body"}
                fontSize={"$7"}
                fontWeight={"$6"}
                color={valueColor}
              >
                {value}
                {rest.unit === "percent" ? "%" : ""}
              </Text>
              <OverviewDiff diff={rest.diff} />
            </XStack>
          )}
          {rest.unit === "point_percent" && (
            <XStack gap={"$2"} alignItems="center">
              <Text
                fontFamily={"$body"}
                fontSize={"$7"}
                fontWeight={"$6"}
                color={valueColor}
              >
                {rest.points}
              </Text>
              <Text
                fontFamily={"$body"}
                fontSize={"$5"}
                fontWeight={"$5"}
                color={"$gray-600"}
              >
                |
              </Text>
              <Text
                fontFamily={"$body"}
                fontSize={"$5"}
                fontWeight={"$5"}
                color={valueColor}
              >
                ({value}%)
              </Text>
            </XStack>
          )}
        </XStack>
        <Progress value={value} variant={rest.variant} height={"$0.5"} />
        <Text
          fontFamily={"$body"}
          fontWeight={500}
          fontSize={"$4"}
          lineHeight={"$3"}
          color={titleColor}
        >
          {title}
        </Text>
      </YStack>
    </Card>
  );
};
